// G12 — Editor da persona (system_prompt) do H.Bot. Só ADMIN (rota adminOnly).
//
// O H.Bot é o agente GENERAL único. Esta tela edita a VOZ/PAPEL dela; o contexto
// dinâmico (nome do usuário, setor, data, memória, busca web, regras anti-injection)
// é injetado automaticamente pelo backend — por isso o card de ajuda explica o que
// NÃO precisa ir no prompt (e o preview mostra como fica montado).

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import assistantPersonaService from '@/services/assistantPersona.service';
import { useToast } from '@/contexts/ToastContext';
import { AssistantPersona } from '@/types/assistant';
import LoadingSpinner from '@/components/LoadingSpinner';

function fmtDate(s: string): string {
  return new Date(s).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

const AdminAssistantPersonaPage: FC = () => {
  const { showToast } = useToast();
  const [persona, setPersona] = useState<AssistantPersona | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Draft editável (separado do carregado pra detectar "sujo").
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState('');
  const [temperature, setTemperature] = useState(0.7);

  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const applyLoaded = useCallback((p: AssistantPersona) => {
    setPersona(p);
    setPrompt(p.system_prompt);
    setModel(p.model);
    setTemperature(p.temperature);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      applyLoaded(await assistantPersonaService.get());
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(
        status === 404
          ? 'Nenhum assistente GENERAL encontrado. Rode o seed (seed_general_agent) antes de configurar a persona.'
          : 'Não foi possível carregar a persona do H.Bot.',
      );
    } finally {
      setLoading(false);
    }
  }, [applyLoaded]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = useMemo(() => {
    if (!persona) return false;
    return (
      prompt !== persona.system_prompt ||
      model !== persona.model ||
      temperature !== persona.temperature
    );
  }, [persona, prompt, model, temperature]);

  const trimmedEmpty = prompt.trim().length === 0;

  const handleSave = useCallback(async () => {
    if (trimmedEmpty) {
      showToast('A persona não pode ficar vazia.', 'error');
      return;
    }
    setSaving(true);
    try {
      const updated = await assistantPersonaService.update({
        system_prompt: prompt,
        model,
        temperature,
      });
      applyLoaded(updated);
      showToast('Persona do H.Bot salva.', 'success');
    } catch (e) {
      const detail = (e as { response?: { data?: Record<string, string> } })?.response?.data;
      const firstMsg = detail ? Object.values(detail)[0] : null;
      showToast(firstMsg || 'Falha ao salvar a persona.', 'error');
    } finally {
      setSaving(false);
    }
  }, [trimmedEmpty, prompt, model, temperature, applyLoaded, showToast]);

  const handleRestore = useCallback(async () => {
    if (!window.confirm('Restaurar a persona oficial do H.Bot? As alterações atuais serão substituídas.')) {
      return;
    }
    setRestoring(true);
    try {
      applyLoaded(await assistantPersonaService.restoreDefault());
      showToast('Persona oficial restaurada.', 'success');
    } catch {
      showToast('Falha ao restaurar a persona.', 'error');
    } finally {
      setRestoring(false);
    }
  }, [applyLoaded, showToast]);

  const handleDiscard = useCallback(() => {
    if (persona) applyLoaded(persona);
  }, [persona, applyLoaded]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="admin-persona-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto mt-10 rounded-xl bg-surface-card border border-surface-border p-6 text-center">
        <span className="material-symbols-outlined text-4xl text-content-muted">auto_awesome</span>
        <p className="mt-3 text-content-secondary">{error}</p>
        <button
          onClick={() => void load()}
          className="mt-4 px-4 py-2 rounded-lg bg-surface-hover text-content-primary text-sm font-medium"
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="admin-persona-page">
      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <span className="material-symbols-outlined text-3xl text-accent">auto_awesome</span>
        <div>
          <h1 className="text-2xl font-bold text-content-primary">Assistente H.Bot — Persona</h1>
          <p className="text-sm text-content-muted mt-1">
            Edite a voz e o comportamento do H.Bot (o <code>system prompt</code>). Esta tela é
            visível só para administradores.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Editor */}
        <div className="lg:col-span-3 space-y-4">
          <div className="rounded-xl bg-surface-card border border-surface-border p-5">
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="persona-prompt" className="text-sm font-semibold text-content-primary">
                Persona (voz e papel do H.Bot)
              </label>
              {persona?.is_default ? (
                <span className="text-xs px-2 py-0.5 rounded-full bg-surface-hover text-content-muted">
                  persona oficial
                </span>
              ) : (
                <span className="text-xs px-2 py-0.5 rounded-full bg-accent/15 text-accent" data-testid="persona-edited-badge">
                  personalizada
                </span>
              )}
            </div>
            <textarea
              id="persona-prompt"
              data-testid="persona-textarea"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={20}
              spellCheck={false}
              className="w-full rounded-lg bg-surface-primary border border-surface-border p-3 text-sm font-mono text-content-primary leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-accent/40"
              placeholder="Você é o H.Bot, assistente da h.lab..."
            />
            {trimmedEmpty && (
              <p className="text-xs text-red-400 mt-1">A persona não pode ficar vazia.</p>
            )}

            {/* Modelo + criatividade */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label htmlFor="persona-model" className="block text-xs font-medium text-content-secondary mb-1">
                  Modelo
                </label>
                <select
                  id="persona-model"
                  data-testid="persona-model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="w-full rounded-lg bg-surface-primary border border-surface-border p-2 text-sm text-content-primary"
                >
                  {persona?.available_models.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="persona-temp" className="block text-xs font-medium text-content-secondary mb-1">
                  Criatividade (temperatura): <span className="font-mono">{temperature.toFixed(1)}</span>
                </label>
                <input
                  id="persona-temp"
                  data-testid="persona-temperature"
                  type="range"
                  min={0}
                  max={2}
                  step={0.1}
                  value={temperature}
                  onChange={(e) => setTemperature(Number(e.target.value))}
                  className="w-full accent-accent"
                />
                <div className="flex justify-between text-2xs text-content-muted">
                  <span>preciso</span>
                  <span>criativo</span>
                </div>
              </div>
            </div>

            {/* Ações */}
            <div className="flex flex-wrap items-center gap-2 mt-5">
              <button
                onClick={() => void handleSave()}
                disabled={!dirty || saving || trimmedEmpty}
                data-testid="persona-save"
                className="px-4 py-2 rounded-lg bg-accent text-accent-contrast text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? 'Salvando…' : 'Salvar persona'}
              </button>
              <button
                onClick={handleDiscard}
                disabled={!dirty || saving}
                className="px-3 py-2 rounded-lg bg-surface-hover text-content-secondary text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Desfazer alterações
              </button>
              <button
                onClick={() => void handleRestore()}
                disabled={restoring || saving}
                data-testid="persona-restore"
                className="px-3 py-2 rounded-lg bg-surface-hover text-content-secondary text-sm ml-auto disabled:opacity-40"
              >
                {restoring ? 'Restaurando…' : 'Restaurar persona oficial'}
              </button>
            </div>
            {persona && (
              <p className="text-2xs text-content-muted mt-3">
                Última atualização: {fmtDate(persona.updated_at)}
              </p>
            )}
          </div>
        </div>

        {/* Ajuda + preview */}
        <div className="lg:col-span-2 space-y-4">
          <div
            className="rounded-xl bg-surface-card border border-surface-border p-5"
            data-testid="persona-help"
          >
            <h2 className="text-sm font-semibold text-content-primary flex items-center gap-2">
              <span className="material-symbols-outlined text-base text-accent">info</span>
              Injetado automaticamente
            </h2>
            <p className="text-xs text-content-muted mt-2">
              Você não precisa escrever isto no prompt — o sistema acrescenta sozinho a cada
              conversa. Remover qualquer coisa daqui <strong>não</strong> quebra o H.Bot (ele continua
              chamando pelo nome, etc.):
            </p>
            <ul className="mt-3 space-y-1.5">
              {persona?.auto_injected.map((item) => (
                <li key={item} className="flex gap-2 text-xs text-content-secondary">
                  <span className="material-symbols-outlined text-sm text-accent">check</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-xl bg-surface-card border border-surface-border p-5">
            <button
              onClick={() => setShowPreview((v) => !v)}
              data-testid="persona-preview-toggle"
              className="w-full flex items-center justify-between text-sm font-semibold text-content-primary"
            >
              <span className="flex items-center gap-2">
                <span className="material-symbols-outlined text-base text-accent">visibility</span>
                Preview do prompt montado
              </span>
              <span className="material-symbols-outlined text-base text-content-muted">
                {showPreview ? 'expand_less' : 'expand_more'}
              </span>
            </button>
            {showPreview && (
              <>
                <p className="text-2xs text-content-muted mt-2">
                  Montado com você como usuário de exemplo (seu nome, setor e a data de hoje),
                  memória vazia. Salve antes pra ver a versão mais recente.
                </p>
                <pre
                  data-testid="persona-preview"
                  className="mt-3 max-h-96 overflow-auto rounded-lg bg-surface-primary border border-surface-border p-3 text-2xs font-mono text-content-secondary whitespace-pre-wrap"
                >
                  {persona?.preview}
                </pre>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAssistantPersonaPage;
